import { useRef, useState, type ReactNode } from 'react'
import { View } from 'react-native'

import { Button } from '@/src/elements/button/default'
import { Input, Textarea } from '@/src/elements/input'
import { Message } from '@/src/elements/message'
import { CommonLayout } from '@/src/elements/templates/CommonLayout'
import { Typography } from '@/src/elements/text/Typography'
import { TEST_ID } from '@/src/general/constants/testId'
import type { Exception } from '@/src/general/errors/Exception'
import { useCaptchaV3 } from '@/src/general/hooks/useCaptcha'
import { useTranslate } from '@/src/lang/useTranslate'

import type { TContactMessagePayload } from '../../api/contactApi'
import { useContactFormMutation } from '../../services/useContactFormMutation'
import { CaptchaV2Modal } from '../components/CaptchaV2Modal'
import { ContactFormValidator, type TContactForm } from '../forms/ContactFormValidator'

export const ContactScreen = (): ReactNode => {
  const [showCaptchaV2Modal, setShowCaptchaV2Modal] = useState(false)
  const [form, setForm] = useState(initialContactForm)
  const { mutateAsync: sendContactMessage } = useContactFormMutation()
  const { t } = useTranslate()

  const { current: formValidator } = useRef(new ContactFormValidator(t))

  const setValueFor = (field: keyof TContactForm) => (value: string) => {
    setForm((prevForm) => ({ ...prevForm, [field]: { value } }))
  }

  const { isLoading, isSuccess } = useCaptchaV3({ onCaptchaChange: setValueFor('captchaV3') })

  const resetFormWithoutCaptcha = () => {
    setForm(({ captchaV3, captchaV2 }) => ({ ...initialContactForm, captchaV3, captchaV2 }))
  }

  const openCaptchaV2ModalWhenErrorIsCaptchaV3 = (error: Exception) => {
    const errorKey = error?.cause?.key ?? error?.cause?.errors?.[0]?.message

    if (
      errorKey === 'captcha.required' ||
      errorKey === 'timeout-or-duplicate' ||
      errorKey === 'score-threshold-not-met'
    ) {
      setShowCaptchaV2Modal(true)
    }
  }

  const submitForm = () => {
    const newForm = formValidator.validate(form)
    setForm(newForm)

    const isValidForm = formValidator.isValidForm(newForm)

    if (isValidForm) {
      const payload: TContactMessagePayload = {
        email: newForm.email.value,
        firstname: newForm.firstname.value,
        lastname: newForm.lastname.value,
        subject: newForm.subject.value,
        message: newForm.message.value,
      }

      if (newForm.captchaV2.value) {
        payload.captchaV2 = newForm.captchaV2.value
      } else {
        payload.captchaV3 = newForm.captchaV3.value
      }

      sendContactMessage(payload).then(resetFormWithoutCaptcha).catch(openCaptchaV2ModalWhenErrorIsCaptchaV3)
    }
  }

  const handleCaptchaV2Complete = (token: string) => {
    setShowCaptchaV2Modal(false)

    const updatedForm = { ...form, captchaV2: { value: token } }
    const newForm = formValidator.validate(updatedForm)
    setForm(newForm)

    if (formValidator.isValidForm(newForm)) {
      const payload: TContactMessagePayload = {
        email: newForm.email.value,
        firstname: newForm.firstname.value,
        lastname: newForm.lastname.value,
        subject: newForm.subject.value,
        message: newForm.message.value,
        captchaV2: newForm.captchaV2.value,
      }

      sendContactMessage(payload)
        .then(() => setForm(({ captchaV3, captchaV2 }) => ({ ...initialContactForm, captchaV3, captchaV2 })))
        .catch(() => {})
    }
  }

  const handleCaptchaV2Close = () => {
    setShowCaptchaV2Modal(false)
  }

  const isBtnLoading = () => {
    if (isLoading) return true
    if (isSuccess) return false
  }

  return (
    <CommonLayout testID={TEST_ID.CONTACT.SCREEN}>
      <View className="container max-w-screen-sm flex-1 gap-4 px-4">
        <Typography black size="2xl" className="py-9 text-primary-page-title">
          {t('words.contact-us')}
        </Typography>

        <Typography>{t('contact.subtitle')}</Typography>

        <Input
          isRequired
          testID={TEST_ID.CONTACT.EMAIL_INPUT}
          label={t('words.your-email-address')}
          placeholder={t('word.email')}
          value={form.email.value}
          onChangeText={setValueFor('email')}
          errorMessage={form.email.error}
        />

        <Input
          isRequired
          testID={TEST_ID.CONTACT.FIRSTNAME_INPUT}
          label={t('word.your-firstname')}
          placeholder={t('contact.firstname.placeholder')}
          value={form.firstname.value}
          onChangeText={setValueFor('firstname')}
          errorMessage={form.firstname.error}
        />

        <Input
          isRequired
          testID={TEST_ID.CONTACT.LASTNAME_INPUT}
          label={t('contact.lastname.label')}
          placeholder={t('contact.lastname.placeholder')}
          value={form.lastname.value}
          onChangeText={setValueFor('lastname')}
          errorMessage={form.lastname.error}
        />

        <Input
          isRequired
          testID={TEST_ID.CONTACT.SUBJECT_INPUT}
          label={t('contact.subject.label')}
          placeholder={t('contact.subject.placeholder')}
          value={form.subject.value}
          onChangeText={setValueFor('subject')}
          errorMessage={form.subject.error}
        />

        <Message className="p-4">{t('contact.message.alert')}</Message>

        <Textarea
          isRequired
          testID={TEST_ID.CONTACT.MESSAGE_INPUT}
          label={t('contact.message.label')}
          placeholder={t('words.your-message')}
          numberOfLines={4}
          value={form.message.value}
          onChangeText={setValueFor('message')}
          errorMessage={form.message.error}
        />

        <Button isLoading={isBtnLoading()} onPress={submitForm} variant="solid" colors="primary" className="self-end">
          {t('word.send')}
        </Button>
      </View>

      <CaptchaV2Modal
        isOpen={showCaptchaV2Modal}
        onCaptchaComplete={handleCaptchaV2Complete}
        onClose={handleCaptchaV2Close}
      />
    </CommonLayout>
  )
}

const initialContactForm: TContactForm = {
  email: { value: '' },
  firstname: { value: '' },
  lastname: { value: '' },
  subject: { value: '' },
  message: { value: '' },
  captchaV3: { value: '' },
  captchaV2: { value: '' },
}
